import clsx from "clsx";
import type { ReactNode } from "react";
import { Icon, type IconName } from "../Icon/Icon";
import styles from "./EmptyState.module.css";

interface EmptyStateBaseProps {
  /** What belongs here: "No lots yet". */
  title: ReactNode;
  /** One or two sentences on how this fills up. */
  description?: ReactNode;
  /**
   * The actions that fill it: "Receive stock", "Paste an order". Leave out any the page header or
   * toolbar already shows; the same button never appears twice.
   */
  actions?: ReactNode;
  /** Heading level of the title (default 2). */
  headingLevel?: 2 | 3;
  className?: string;
  children?: ReactNode;
}

export type EmptyStateProps = EmptyStateBaseProps &
  (
    | {
        /** A whole empty page: left-aligned text, no icon (a lone glyph there reads as a button). */
        variant?: "page";
        icon?: never;
      }
    | {
        /** Inside a table or card: centred, with an optional icon above the title. */
        variant: "inline";
        icon?: IconName;
      }
  );

/** An empty screen is an invitation to act: say what belongs here and offer the action that fills it. */
export function EmptyState({
  title,
  description,
  icon,
  actions,
  variant = "page",
  headingLevel = 2,
  className,
  children,
}: EmptyStateProps) {
  const Heading = `h${headingLevel}` as const;
  return (
    <div className={clsx(styles.empty, className)} data-variant={variant}>
      {icon ? (
        <span className={styles.icon}>
          <Icon name={icon} size="md" />
        </span>
      ) : null}
      <Heading className={styles.title}>{title}</Heading>
      {description ? <p className={styles.description}>{description}</p> : null}
      {children}
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  );
}
